import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose, setPropTypes, withState, withHandlers } from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import {
  Button,
  CardMedia,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography
} from '@material-ui/core';
import { bindActionCreators } from 'redux';
import { max } from 'lodash';
import { sendLogin, uploadFile } from '../../auth/Auth';
import { reIdentity } from '../../actions/Accounts';
import {
  editIdentityGroup,
  EDIT_IDENTITY_GROUP_SUCCESS
} from '../../actions/Identity';
import ProgressDialog from '../../components/molecules/ProgressMobileDialog';
import StepProgressBar from '../../components/atoms/StepProgressBar';
import { editGroup, EDIT_GROUP_SUCCESS } from '../../actions/Groups';

const styles = theme => ({
  hidden: {
    display: 'none'
  },
  page: {
    paddingLeft: 15,
    paddingRight: 15
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 20,
    paddingTop: theme.spacing(1)
  },
  titleGroup: {
    marginTop: 20,
    marginBottom: 20,
    display: 'flex',
    alignItems: 'flex-start'
  },
  pagerInDiv: {
    padding: 20
  },
  redText: {
    color: '#FF0000'
  },
  tableTitle: {
    fontSize: 16,
    marginBottom: '20px'
  },
  detailTable: {
    width: '100%'
  },
  tableCellItem: {
    borderBottom: '1px solid #E0E0E0',
    backgroundColor: '#F7F7F7',
    width: '115px',
    padding: '13px 8px',
    fontSize: 12,
    fontWeight: 700
  },
  tableCellData: {
    borderBottom: '1px solid #E0E0E0',
    padding: '13px 8px',
    fontSize: 12,
    fontWeight: 500
  },
  noteText: {
    fontSize: 12,
    paddingTop: 10,
    fontWeight: 600
  },
  textLabel: {
    fontSize: 12,
    fontWeight: 600
  },
  description: {
    color: '#707070',
    fontSize: 14
  },
  mb10: {
    marginBottom: 10
  },
  alignItemsStart: {
    alignItems: 'start'
  },
  linkToPrev: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 20
  },
  image: {
    marginBottom: 5
  }
});

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      account: state.accounts.account,
      groupDetail: state.groups.detail,
      isReIdentity: state.accounts.reIdentity
    }),
    dispatch => ({
      actions: bindActionCreators(
        {
          editGroup,
          editIdentityGroup,
          reIdentity
        },
        dispatch
      )
    })
  ),
  setPropTypes(propTypes),
  withState('isRegisterSuccess', 'updateIsRegisterSuccess', false),
  withState('openRegisterProgress', 'updateOpenRegisterProgress', false),
  withState('loading', 'updateLoading', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('titleMessage', 'setTitleMessage', ''),
  withState('btnText', 'setBtnText', null),
  withState('previewImageBank', 'setPreviewImageBank', null),
  withState('previewImageRegisterCopy', 'setPreviewImageRegisterCopy', []),
  withState('previewImageLicense', 'setPreviewImageLicense', []),
  withState('previewImageSeal', 'setPreviewImageSeal', []),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    }
  })
);

const Main = enhance(
  ({
    classes,
    actions,
    checkSession,
    show,
    proceedToNext,
    handleBack,
    groupDetail,
    isReIdentity,
    identityId,
    groupParams,
    identityParams,
    previewImageBank,
    setPreviewImageBank,
    previewImageRegisterCopy,
    setPreviewImageRegisterCopy,
    previewImageLicense,
    setPreviewImageLicense,
    previewImageSeal,
    setPreviewImageSeal,
    isRegisterSuccess,
    updateIsRegisterSuccess,
    openRegisterProgress,
    updateOpenRegisterProgress,
    loading,
    updateLoading,
    resultMessage,
    setResultMessage,
    titleMessage,
    setTitleMessage,
    btnText,
    setBtnText
  }) => {
    useEffect(() => {
      if (show) {
        if (groupParams.bankImage) {
          const reader = new FileReader();
          reader.readAsDataURL(groupParams.bankImage);
          reader.onloadend = () => {
            setPreviewImageBank(reader.result);
          };
        }

        if (identityParams.registerCopy) {
          // Remove empty items
          const registerCopyList = identityParams.registerCopy.filter(i => i);
          const loadImgPromises = [];
          for (let i = 0; i < registerCopyList.length; i += 1) {
            loadImgPromises.push(
              new Promise(resolve => {
                const reader = new FileReader();
                reader.readAsDataURL(registerCopyList[i]);
                reader.onloadend = () => {
                  resolve(reader.result);
                };
              })
            );
          }
          Promise.all(loadImgPromises).then(imgs => {
            setPreviewImageRegisterCopy(imgs);
          });
        }

        if (identityParams.businessLicense) {
          // Remove empty items
          const businessLicenseList = identityParams.businessLicense.filter(
            i => i
          );
          const loadImgPromises = [];
          for (let i = 0; i < businessLicenseList.length; i += 1) {
            loadImgPromises.push(
              new Promise(resolve => {
                const reader = new FileReader();
                reader.readAsDataURL(businessLicenseList[i]);
                reader.onloadend = () => {
                  resolve(reader.result);
                };
              })
            );
          }
          Promise.all(loadImgPromises).then(imgs => {
            setPreviewImageLicense(imgs);
          });
        }

        if (identityParams.sealCertificate) {
          // Remove empty items
          const sealCertificateList = identityParams.sealCertificate.filter(
            i => i
          );
          const loadImgPromises = [];
          for (let i = 0; i < sealCertificateList.length; i += 1) {
            loadImgPromises.push(
              new Promise(resolve => {
                const reader = new FileReader();
                reader.readAsDataURL(sealCertificateList[i]);
                reader.onloadend = () => {
                  resolve(reader.result);
                };
              })
            );
          }
          Promise.all(loadImgPromises).then(imgs => {
            setPreviewImageSeal(imgs);
          });
        }
      }
    }, [show]);

    const groupDisplayLimit =
      groupParams.groupType === 'CAMPAIGN' ||
      groupParams.groupType === 'OTHERS' ||
      groupParams.groupType === 'GOCHI_KID';

    const buildGroupParams = async () => {
      const paramsEdit = { groupType: groupParams.groupType };
      if (
        paramsEdit.groupType === 'CAMPAIGN' ||
        paramsEdit.groupType === 'OTHERS' ||
        paramsEdit.groupType === 'GOCHI_KID'
      ) {
        paramsEdit.depositManage = 'NOT_MANAGE';
      } else {
        paramsEdit.depositManage = groupParams.depositManage;
      }
      const bankAccount = {};
      if (paramsEdit.depositManage === 'NONSELF') {
        bankAccount.bankName = groupParams.bankName;
        bankAccount.bankBranchName = groupParams.bankBranchName;
        bankAccount.bankAccountType = groupParams.bankAccountType;
        bankAccount.bankAccountNumber = groupParams.bankAccountNumber;
        bankAccount.bankAccountHolder = groupParams.bankAccountHolder;
        bankAccount.bankCode = groupParams.bankCode;
        bankAccount.bankBranchCode = groupParams.bankBranchCode;
        if (groupParams.bankImage) {
          bankAccount.bankImage = await uploadFile(
            'identity',
            identityId,
            groupParams.bankImage,
            'public'
          );
        }
      }
      paramsEdit.bankAccount = bankAccount;
      return { ...groupDetail, ...paramsEdit };
    };

    const registerAction = async () => {
      updateLoading(true);
      updateOpenRegisterProgress(true);

      const editIdentityParams = {};
      let sealPath = [];
      let licensePath = [];
      let registerCopyPath = [];

      try {
        // Params from step 1.1
        if (!isReIdentity) {
          const editGroupResult = await actions
            .editGroup(groupDetail.groupId, await buildGroupParams())
            .then(data => checkSession(data));
          if (editGroupResult.type !== EDIT_GROUP_SUCCESS) {
            throw new Error('edit group failure');
          }
          editIdentityParams.note = identityParams.note;
        }

        // Params from step 1.2
        if (
          identityParams.sealCertificate &&
          identityParams.businessLicense &&
          identityParams.registerCopy
        ) {
          // Remove empty items
          const sealCertificateList = identityParams.sealCertificate.filter(
            i => i
          );
          const registerCopyList = identityParams.registerCopy.filter(i => i);
          const businessLicenseList = identityParams.businessLicense.filter(
            i => i
          );
          const businessLicenseNameList = identityParams.businessLicenseName.filter(
            i => i
          );

          if (sealCertificateList.length > 0) {
            const imagePaths = await Promise.all(
              sealCertificateList.map(async image => {
                if (image !== null) {
                  // if (image.size > Number(process.env.REACT_APP_RESIZE_BORDER)) {
                  //   const { resizeImageFile } = await resizeImage(image);
                  //   image = resizeImageFile;
                  // }
                  const imagePath = await uploadFile(
                    'identity',
                    identityId,
                    image,
                    'public'
                  );
                  return imagePath;
                }
                return null;
              })
            );
            sealPath = imagePaths
              .filter(i => i !== null)
              .map((i, index) => {
                return { image: i, viewOrder: index };
              });
          }
          if (businessLicenseList.length > 0) {
            const imagePaths = await Promise.all(
              businessLicenseList.map(async image => {
                if (image !== null) {
                  const imagePath = await uploadFile(
                    'identity',
                    identityId,
                    image,
                    'public'
                  );
                  return imagePath;
                }
                return null;
              })
            );
            licensePath = imagePaths
              .filter(i => i !== null)
              .map((i, index) => {
                return { image: i, viewOrder: index };
              });
          }
          if (registerCopyList.length > 0) {
            const imagePaths = await Promise.all(
              registerCopyList.map(async image => {
                if (image !== null) {
                  const imagePath = await uploadFile(
                    'identity',
                    identityId,
                    image,
                    'public'
                  );
                  return imagePath;
                }
                return null;
              })
            );
            registerCopyPath = imagePaths
              .filter(i => i !== null)
              .map((i, index) => {
                return { image: i, viewOrder: index };
              });
          }

          const identityImages = [];
          const maxLengthImage = max([
            sealCertificateList.length,
            businessLicenseList.length,
            registerCopyList.length
          ]);
          if (maxLengthImage) {
            for (let i = 0; i < maxLengthImage; i += 1) {
              identityImages.push({
                sealCertificate: sealPath[i] ? sealPath[i].image : '',
                registeredCopy: registerCopyPath[i]
                  ? registerCopyPath[i].image
                  : '',
                other: licensePath[i] ? licensePath[i].image : '',
                note:
                  licensePath[i] &&
                  businessLicenseNameList[licensePath[i].viewOrder]
                    ? businessLicenseNameList[licensePath[i].viewOrder]
                    : ''
              });
            }
          }
          editIdentityParams.identityImages = identityImages;
        }

        editIdentityParams.representativeName =
          identityParams.representativeName;
        editIdentityParams.representativeNameKana =
          identityParams.representativeNameKana;
        editIdentityParams.companyCode = identityParams.companyCode;

        const editIdentityResult = await actions
          .editIdentityGroup(identityId, editIdentityParams)
          .then(data => checkSession(data));
        if (editIdentityResult.type !== EDIT_IDENTITY_GROUP_SUCCESS) {
          throw new Error('edit identity failure');
        }
        // If no error then show success message
        updateIsRegisterSuccess(true);
        setTitleMessage('審査書類提出完了');
        setResultMessage(
          <>
            審査書類の提出が完了しました。事務局にて審査を行いますので、いましばらくお待ちください。審査結果はメールで通知いたします。
            <br />
            審査が完了するまでの間に、
            <br />
            ・グループ情報登録
            <br />
            が可能ですので、ぜひ情報を充実いただけますよう、お願いいたします。
          </>
        );
        setBtnText('各種情報を入力する');
      } catch (err) {
        setResultMessage('登録に失敗しました。');
      }
      updateLoading(false);
    };

    const progressOkAction = () => {
      if (isRegisterSuccess) {
        // TODO: action.getIdentites()
        actions.reIdentity(false);
        proceedToNext();
      }
      updateOpenRegisterProgress(false);
    };

    const getGroupTypeStr = mode => {
      switch (mode) {
        case 'CHAIN_SHOP':
          return 'チェーン店';
        case 'COMMERCIAL_COMPLEX':
          return '商業施設';
        case 'CAMPAIGN':
          return 'キャンペーン';
        default:
          return 'その他/仮想店舗';
      }
    };

    const getDepositManageStr = mode => {
      switch (mode) {
        case 'NONSELF':
          return '銀行口座を登録する';
        default:
          return 'グループでは入金管理を登録しない';
      }
    };

    const getBankAccountTypeStr = mode => {
      switch (mode) {
        case 'SAVINGS':
          return '普通預金';
        default:
          return '当座預金';
      }
    };

    return (
      <div className={show ? null : classes.hidden}>
        <div className={classes.page}>
          <div className={classes.titleGroup}>
            <Typography component="h2" variant="h5">
              審査申請情報の確認
            </Typography>
          </div>

          <StepProgressBar currentStep={2} />

          <Paper className={classes.paper} elevation={1}>
            <div className={classes.pagerInDiv}>
              <p className={classes.tableTitle}>
                審査申請情報をご確認ください。
              </p>
              <Table className={classes.detailTable}>
                <TableBody>
                  {!isReIdentity && (
                    <>
                      <TableRow>
                        <TableCell className={classes.tableCellItem}>
                          グループ種別の選択
                        </TableCell>
                        <TableCell className={classes.tableCellData}>
                          {getGroupTypeStr(groupParams.groupType)}
                        </TableCell>
                      </TableRow>
                      {!groupDisplayLimit && (
                        <TableRow>
                          <TableCell className={classes.tableCellItem}>
                            入金管理方法の選択
                          </TableCell>
                          <TableCell className={classes.tableCellData}>
                            {getDepositManageStr(groupParams.depositManage)}
                          </TableCell>
                        </TableRow>
                      )}
                      {groupParams.depositManage === 'NONSELF' && (
                        <>
                          <TableRow>
                            <TableCell className={classes.tableCellItem}>
                              銀行名
                            </TableCell>
                            <TableCell className={classes.tableCellData}>
                              {groupParams.depositManage === 'NONSELF'
                                ? groupParams.bankName
                                : ''}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.tableCellItem}>
                              支店名
                            </TableCell>
                            <TableCell className={classes.tableCellData}>
                              {groupParams.depositManage === 'NONSELF'
                                ? groupParams.bankBranchName
                                : ''}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.tableCellItem}>
                              口座種別
                            </TableCell>
                            <TableCell className={classes.tableCellData}>
                              {groupParams.depositManage === 'NONSELF'
                                ? getBankAccountTypeStr(
                                    groupParams.bankAccountType
                                  )
                                : ''}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.tableCellItem}>
                              口座番号
                            </TableCell>
                            <TableCell className={classes.tableCellData}>
                              {groupParams.depositManage === 'NONSELF'
                                ? groupParams.bankAccountNumber
                                : ''}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.tableCellItem}>
                              口座名義
                            </TableCell>
                            <TableCell className={classes.tableCellData}>
                              {groupParams.depositManage === 'NONSELF'
                                ? groupParams.bankAccountHolder
                                : ''}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.tableCellItem}>
                              振込先銀行通帳の1枚目画像
                            </TableCell>
                            <TableCell className={classes.tableCellData}>
                              <CardMedia
                                component="img"
                                image={previewImageBank}
                              />
                            </TableCell>
                          </TableRow>
                        </>
                      )}
                      <TableRow>
                        <TableCell className={classes.tableCellItem}>
                          連絡欄
                        </TableCell>
                        <TableCell className={classes.tableCellData}>
                          {identityParams.note}
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                  {identityParams.registerCopy && (
                    <TableRow>
                      <TableCell className={classes.tableCellItem}>
                        登記簿謄本
                      </TableCell>
                      <TableCell className={classes.tableCellData}>
                        {previewImageRegisterCopy.map(item => (
                          <CardMedia
                            component="img"
                            image={item}
                            className={classes.image}
                          />
                        ))}
                      </TableCell>
                    </TableRow>
                  )}
                  {identityParams.sealCertificate && (
                    <TableRow>
                      <TableCell className={classes.tableCellItem}>
                        印鑑証明
                      </TableCell>
                      <TableCell className={classes.tableCellData}>
                        {previewImageSeal.map(item => (
                          <CardMedia
                            component="img"
                            image={item}
                            className={classes.image}
                          />
                        ))}
                      </TableCell>
                    </TableRow>
                  )}
                  {identityParams.businessLicense && (
                    <TableRow>
                      <TableCell className={classes.tableCellItem}>
                        その他資料の添付
                      </TableCell>
                      <TableCell className={classes.tableCellData}>
                        {previewImageLicense.map(item => (
                          <CardMedia
                            component="img"
                            image={item}
                            className={classes.image}
                          />
                        ))}
                      </TableCell>
                    </TableRow>
                  )}
                  {typeof identityParams.representativeName !== 'undefined' && (
                    <TableRow>
                      <TableCell className={classes.tableCellItem}>
                        代表者名
                      </TableCell>
                      <TableCell className={classes.tableCellData}>
                        {identityParams.representativeName}
                      </TableCell>
                    </TableRow>
                  )}
                  {typeof identityParams.representativeNameKana !==
                    'undefined' && (
                    <TableRow>
                      <TableCell className={classes.tableCellItem}>
                        代表者名かな
                      </TableCell>
                      <TableCell className={classes.tableCellData}>
                        {identityParams.representativeNameKana}
                      </TableCell>
                    </TableRow>
                  )}
                  {typeof identityParams.companyCode !== 'undefined' && (
                    <TableRow>
                      <TableCell className={classes.tableCellItem}>
                        法人番号
                      </TableCell>
                      <TableCell className={classes.tableCellData}>
                        {identityParams.companyCode}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            <div className={classes.pagerInDiv}>
              <Button
                type="button"
                fullWidth
                variant="contained"
                color="primary"
                onClick={registerAction}
              >
                入力情報を確認
              </Button>
            </div>
          </Paper>
        </div>
        <div className={classes.linkToPrev}>
          <Button color="primary" onClick={handleBack}>
            前の画面に戻る
          </Button>
        </div>
        <ProgressDialog
          openProgress={openRegisterProgress}
          progressOkAction={progressOkAction}
          loading={loading}
          resultMessage={resultMessage}
          title={titleMessage}
          btnText={btnText}
        />
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
